import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { invaliderFiches } from '../../lib/cache'
import { titreGenerique } from '../../lib/titres'
import { api, ApiError, type Category, type Item, type LinkPreview, type NewItem } from '../../lib/api'
import { CategoryMark } from '../spaces/CategoryMark'
import { ChoixDImage } from './ChoixDImage'

type Tuile = 'lien' | 'note'

/**
 * Le presse-papier n'existe que dans un « contexte sécurisé » : en HTTPS, ou sur localhost. Tant
 * que l'application est servie en clair (point ouvert P1), `navigator.clipboard` est absent et le
 * bouton « Coller » échouait à chaque appui. Mieux vaut ne pas l'afficher qu'afficher un bouton
 * qui ne peut pas fonctionner. Il réapparaîtra seul le jour où le site passera en HTTPS.
 */
const presseAPapierDisponible =
  typeof navigator !== 'undefined' && typeof navigator.clipboard?.readText === 'function'

/**
 * Planche 08 du cahier des charges — feuille d'ajout, arbitrage `1d` : « 4 tuiles de type,
 * puis un champ unique ».
 *
 * Deux écarts consignés :
 *  - la tuile « Coller le lien copié » remplace le bandeau presse-papier de la maquette
 *    (décision L5) : sur iOS, lire le presse-papier exige un geste de l'utilisateur, donc un
 *    bandeau qui apparaît seul n'est pas réalisable ;
 *  - la tuile Photo arrive à l'étape 3b, avec le stockage de médias. Elle n'est pas affichée
 *    désactivée : principe 6, un module non livré ne laisse aucune trace.
 */
/** Ce avec quoi la feuille s'ouvre, quand elle n'est pas ouverte vide. */
export type DepartDAjout = {
  readonly tuile: Tuile
  /** L'adresse pour un lien, le titre pour une note. */
  readonly valeur: string
  readonly note: string
}

export function AddSheet({
  spaceId,
  onClose,
  defaultCategoryId,
  depart,
  entete,
}: {
  spaceId: string
  onClose: () => void
  /**
   * Catégorie du contexte d'où vient l'ajout : ajouter depuis « Recettes » range dans Recettes,
   * sans avoir à classer après coup. Le contexte explicite prime sur la catégorie devinée à
   * partir du domaine — l'utilisateur qui est dans un rayon sait mieux que la règle.
   */
  defaultCategoryId?: string | null
  /**
   * Contenu déjà connu : un partage venu du téléphone. La feuille s'ouvre alors directement sur
   * le formulaire rempli, sans redemander ce qu'on vient de lui donner.
   */
  depart?: DepartDAjout
  /**
   * Un mot posé en tête de la feuille — « Dans Gil&Val », avec de quoi en changer. Rendu
   * **dedans** et non autour : la feuille est une surface flottante qui couvre tout l'écran,
   * et ce qui est placé derrière elle est voilé et hors d'atteinte.
   */
  entete?: ReactNode
}) {
  const [tuile, setTuile] = useState<Tuile | null>(depart?.tuile ?? null)
  const navigate = useNavigate()

  const { data: categories } = useQuery<readonly Category[]>({
    queryKey: ['espace', spaceId, 'categories'],
    queryFn: () => api.space(spaceId).then((detail) => detail.categories),
    staleTime: 60_000,
  })

  const contexte = categories?.find((category) => category.id === defaultCategoryId) ?? null

  if (tuile !== null) {
    return (
      <Formulaire
        spaceId={spaceId}
        titre={tuile === 'lien' ? 'Nouveau lien' : 'Nouvelle note'}
        onClose={onClose}
        onRetour={() => setTuile(null)}
        champs={tuile}
        defaultCategoryId={defaultCategoryId ?? null}
        contexte={contexte}
        valeurInitiale={depart?.tuile === tuile ? depart.valeur : ''}
        noteInitiale={depart?.tuile === tuile ? depart.note : ''}
        entete={entete}
      />
    )
  }

  return (
    <Feuille onClose={onClose}>
      {entete}

      <h2 className="font-serif text-record">
        {contexte ? `Ajouter dans ${contexte.name}` : 'Ajouter'}
      </h2>

      {/* « Coller le lien copié » ne figure pas ici : elle faisait doublon avec « Lien », les
          deux ouvrant le même formulaire. Le cahier place « Coller » en bouton à côté du champ
          (planche 09), pas en type d'ajout — c'est un raccourci de saisie, pas une nature. */}
      <div className="mt-4 grid grid-cols-2" style={{ gap: 12 }}>
        <Tuile libelle="Lien" onClick={() => setTuile('lien')} />
        <Tuile libelle="Note" onClick={() => setTuile('note')} />
      </div>

      {/* Les trois autres gestes quotidiens. Ils ne créent pas une fiche mais une ligne dans
          leur module : ils mènent donc à leur écran, formulaire déjà ouvert, plutôt que de
          recopier ici trois formulaires qui divergeraient du jour au lendemain. */}
      <p className="mt-5 text-label font-semibold uppercase text-ink-muted">Ailleurs</p>
      <div className="mt-2 grid grid-cols-3" style={{ gap: 12 }}>
        {[
          { libelle: 'Rendez-vous', vers: `/espace/${spaceId}/agenda?ajout=1` },
          { libelle: 'Courses', vers: `/espace/${spaceId}/courses?ajout=1` },
          { libelle: 'Dépense', vers: `/espace/${spaceId}/depenses?ajout=1` },
        ].map((raccourci) => (
          <button
            key={raccourci.libelle}
            type="button"
            onClick={() => {
              onClose()
              void navigate(raccourci.vers)
            }}
            className="rounded-card border border-border bg-card px-2 text-meta font-semibold"
            style={{ height: 64 }}
          >
            {raccourci.libelle}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onClose}
        className="mt-4 w-full rounded-button border border-border bg-card text-card-title font-semibold"
        style={{ height: 56 }}
      >
        Annuler
      </button>
    </Feuille>
  )
}

/** Tuiles de 104 px : « cibles énormes, aucune lecture nécessaire » (arbitrage `1d`). */
function Tuile({ libelle, onClick }: { libelle: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-end rounded-card border border-border bg-card p-4 text-left text-card-title font-semibold"
      style={{ height: 104 }}
    >
      {libelle}
    </button>
  )
}

function Formulaire({
  spaceId,
  titre,
  champs,
  onClose,
  onRetour,
  defaultCategoryId,
  contexte,
  valeurInitiale,
  noteInitiale,
  entete,
}: {
  spaceId: string
  titre: string
  champs: 'lien' | 'note'
  onClose: () => void
  onRetour: () => void
  defaultCategoryId: string | null
  contexte: Category | null
  valeurInitiale: string
  noteInitiale: string
  entete?: ReactNode
}) {
  const [valeur, setValeur] = useState(valeurInitiale)
  const [note, setNote] = useState(noteInitiale)
  const [nomDonne, setNomDonne] = useState('')
  const [images, setImages] = useState<File[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [enregistre, setEnregistre] = useState<Item | null>(null)
  const queryClient = useQueryClient()

  // Planche 09 : « la prévisualisation se charge en arrière-plan, bouton actif immédiatement ».
  // L'aperçu ne conditionne donc jamais l'enregistrement — il l'enrichit s'il arrive à temps.
  const url = champs === 'lien' ? valeur.trim() : ''
  const apercu = useQuery<LinkPreview>({
    queryKey: ['apercu', spaceId, url],
    queryFn: () => api.linkPreview(spaceId, url),
    enabled: /^https?:\/\/\S+\.\S+/.test(url),
    retry: false,
    staleTime: 5 * 60_000,
  })

  const creer = useMutation({
    mutationFn: (): Promise<Item> =>
      api.createItem(
        spaceId,
        champs === 'lien'
          ? ({
              type: 'link',
              url: valeur.trim(),
              // Le nom donné à la main prime : il n'existe que quand la page n'a rien dit
              // d'utile, et il est alors la seule chose qui distingue cette fiche.
              ...(nomDonne.trim().length > 0
                ? { title: nomDonne.trim() }
                : apercu.data?.resolved
                  // Ce que le serveur a lu, pas ce que le client invente.
                  ? { title: apercu.data.title }
                  : {}),
              ...(apercu.data?.imageUrl ? { imageUrl: apercu.data.imageUrl } : {}),
              // Le contexte d'où l'on ajoute prime sur la catégorie devinée.
              ...(defaultCategoryId
                ? { categoryId: defaultCategoryId }
                : apercu.data?.suggestedCategoryId
                  ? { categoryId: apercu.data.suggestedCategoryId }
                  : {}),
            } satisfies NewItem)
          : ({
              type: 'note',
              title: valeur.trim(),
              description: note.trim(),
              ...(defaultCategoryId ? { categoryId: defaultCategoryId } : {}),
            } satisfies NewItem),
      ),
    onSuccess: async (item) => {
      // Les images ne peuvent partir qu'après la création : elles s'attachent à une fiche, et la
      // fiche n'existe pas avant. Un échec d'envoi n'annule pas la fiche — mieux vaut une note
      // sans son image qu'une note perdue.
      for (const fichier of images) {
        try {
          await api.uploadImage(spaceId, item.id, fichier)
        } catch (cause) {
          setErreur(
            cause instanceof ApiError
              ? `Note enregistrée, mais une image a été refusée : ${cause.message}`
              : 'Note enregistrée, mais une image n\'a pas pu être envoyée.',
          )
        }
      }

      setImages([])
      setEnregistre(item)
      // Toutes les vues qui montrent des fiches, pas seulement l'espace : sans la liste des
      // fiches, un ajout fait depuis une catégorie n'y apparaissait qu'au rafraîchissement
      // suivant, jusqu'à une minute plus tard.
      await invaliderFiches(queryClient, spaceId)
      // Vibration légère à l'enregistrement — section 03.6, « rien d'autre ».
      navigator.vibrate?.(10)
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'enregistrement a échoué."),
  })

  if (enregistre) {
    return <BandeauEnregistre spaceId={spaceId} item={enregistre} onClose={onClose} />
  }

  return (
    <Feuille onClose={onClose}>
      {entete}

      <div className="flex items-center justify-between">
        <button type="button" onClick={onRetour} className="text-meta font-semibold text-accent">
          Retour
        </button>
        <h2 className="font-serif text-record">{titre}</h2>
        <span style={{ width: 48 }} />
      </div>

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="valeur">
        {champs === 'lien' ? 'Adresse du lien' : 'Titre'}
      </label>
      <div className="mt-2 flex gap-2">
        <input
          id="valeur"
          autoFocus
          inputMode={champs === 'lien' ? 'url' : 'text'}
          placeholder={champs === 'lien' ? 'https://marmiton.org/…' : 'Expo photo au Grand Palais'}
          value={valeur}
          onChange={(event) => {
            setValeur(event.target.value)
            setErreur(null)
          }}
          aria-invalid={erreur !== null}
          className="grow rounded-field border-[1.5px] bg-card px-4 outline-none"
          style={{
            height: 56,
            fontSize: 17,
            borderColor: erreur ? 'var(--color-danger)' : 'var(--color-accent)',
          }}
        />

        {/* Planche 09 : « Coller » à côté du champ. Le geste de l'utilisateur autorise la
            lecture du presse-papier ; sur iOS le système demande encore confirmation, ce qui
            est hors de notre contrôle. */}
        {champs === 'lien' && presseAPapierDisponible ? (
          <button
            type="button"
            onClick={async () => {
              try {
                const texte = await navigator.clipboard.readText()
                setValeur(texte.trim())
                setErreur(null)
              } catch {
                setErreur("Le presse-papier n'est pas accessible. Collez à la main.")
              }
            }}
            className="shrink-0 rounded-button border border-border bg-card px-4 text-card-title font-semibold"
            style={{ height: 56 }}
          >
            Coller
          </button>
        ) : null}
      </div>

      {contexte ? (
        <p className="mt-3 flex items-center gap-2 text-meta">
          <CategoryMark initials={contexte.initials} hue={contexte.hue} size="small" />
          <span className="text-ink-muted">Sera rangé dans {contexte.name}.</span>
        </p>
      ) : null}

      {champs === 'lien' && apercu.isFetching ? <ApercuSquelette /> : null}
      {champs === 'lien' && apercu.data && !apercu.isFetching ? (
        <Apercu apercu={apercu.data} spaceId={spaceId} />
      ) : null}

      {/* Quand la page ne dit rien d'elle-même — une story derrière un mur de connexion répond
          « Facebook » et rien d'autre —, la fiche s'appellerait comme le site et ne se
          distinguerait d'aucune autre. On le dit, et on offre le champ pour la nommer : le texte
          annonçait « titre à compléter » sans donner où le faire. */}
      {champs === 'lien' &&
      apercu.data &&
      !apercu.isFetching &&
      titreGenerique(apercu.data.title, apercu.data.domain) ? (
        <>
          <label
            className="mt-4 block text-label font-semibold uppercase text-ink-muted"
            htmlFor="nom-donne"
          >
            Comment l'appeler ?
          </label>
          <input
            id="nom-donne"
            value={nomDonne}
            onChange={(event) => setNomDonne(event.target.value)}
            placeholder="Gâteau au chocolat de Val"
            className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
            style={{ height: 56, fontSize: 17 }}
          />
          <p className="mt-2 text-meta text-ink-muted">
            {apercu.data.domain} ne dit pas de quoi il s'agit. Sans nom, la fiche s'appellera
            « {apercu.data.title} ».
          </p>
        </>
      ) : null}

      {champs === 'note' ? (
        <>
          <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="note">
            Note
          </label>
          {/* Le collage est écouté sur la zone de saisie : c'est là qu'on colle naturellement,
              et l'événement porte les fichiers même sans HTTPS — contrairement à l'API de
              presse-papier asynchrone, qui exige un contexte sécurisé. */}
          <textarea
            id="note"
            rows={4}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onPaste={(event) => {
              const fichiers = Array.from(event.clipboardData.files).filter((f) =>
                f.type.startsWith('image/'),
              )
              if (fichiers.length > 0) {
                event.preventDefault()
                setImages((deja) => [...deja, ...fichiers])
              }
            }}
            className="mt-2 w-full rounded-field border-[1.5px] border-border bg-card p-4 outline-none"
            style={{ fontSize: 16 }}
          />

          <p className="mt-4 text-label font-semibold uppercase text-ink-muted">Images</p>
          <div className="mt-2">
            <ChoixDImage
              libelle="Choisir une image"
              multiple
              onChoix={(choisis) => setImages((deja) => [...deja, ...choisis])}
            />
          </div>
          <p className="mt-2 text-meta text-ink-muted">
            Ou collez une image directement dans la note.
          </p>

          {images.length > 0 ? (
            <ul className="mt-3 flex flex-col gap-2">
              {images.map((fichier, index) => (
                <li
                  key={`${fichier.name}-${index}`}
                  className="flex items-center justify-between gap-3 rounded-field bg-sunken px-3"
                  style={{ minHeight: 40 }}
                >
                  <span className="truncate text-meta">{fichier.name || 'Image collée'}</span>
                  <button
                    type="button"
                    onClick={() => setImages((deja) => deja.filter((_, i) => i !== index))}
                    className="shrink-0 text-meta font-semibold text-ink-muted"
                  >
                    Retirer
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}

      {erreur ? (
        <p className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
          {erreur}
        </p>
      ) : null}

      <button
        type="button"
        disabled={valeur.trim().length === 0 || creer.isPending}
        onClick={() => creer.mutate()}
        className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: 56 }}
      >
        {creer.isPending ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </Feuille>
  )
}

/**
 * Aperçu récupéré — planche 09 : image, titre, source, et catégorie devinée avec sa raison.
 * La raison est affichée parce que le cahier exige un classement « explicable ».
 */
function Apercu({ apercu, spaceId }: { apercu: LinkPreview; spaceId: string }) {
  // Clé distincte de ['espace', spaceId] : celle-ci porte le détail complet, celle-là les
  // seules catégories. Partager la clé ferait servir une forme pour l'autre.
  const { data: categories } = useQuery<readonly Category[]>({
    queryKey: ['espace', spaceId, 'categories'],
    queryFn: () => api.space(spaceId).then((detail) => detail.categories),
    staleTime: 60_000,
  })

  const devinee = categories?.find((category) => category.id === apercu.suggestedCategoryId)

  return (
    <section className="mt-4 overflow-hidden rounded-card border border-border bg-card">
      {apercu.imageUrl ? (
        // Ratio 4:3, comme l'emplacement d'image de la section 03.6.
        <img
          src={apercu.imageUrl}
          alt=""
          className="w-full object-cover"
          style={{ aspectRatio: '4 / 3' }}
        />
      ) : null}

      <div className="p-4">
        <p className="text-card-title font-semibold">{apercu.title}</p>
        <p className="mt-1 text-meta text-ink-muted">
          {apercu.domain}
          {/* Le champ de nom apparaît juste en dessous : la mention promet quelque chose qui
              existe, ce qui n'était pas le cas. */}
          {titreGenerique(apercu.title, apercu.domain)
            ? ' · ce titre ne dit rien, nommez la fiche'
            : ''}
        </p>

        {devinee ? (
          <p className="mt-3 flex items-center gap-2 text-meta">
            <span
              className="flex items-center justify-center rounded-field text-card"
              style={{
                width: 32,
                height: 32,
                fontSize: 13,
                background: `var(--color-cat-${devinee.hue}-mark)`,
              }}
            >
              {devinee.initials}
            </span>
            <span>
              {devinee.name}
              <span className="text-ink-muted"> · {apercu.suggestionReason}</span>
            </span>
          </p>
        ) : (
          <p className="mt-3 text-meta text-ink-muted">Sera rangé plus tard.</p>
        )}
      </div>
    </section>
  )
}

/** « Squelette pendant la récupération », de même géométrie que l'aperçu réel (planche 19). */
function ApercuSquelette() {
  return (
    <section className="mt-4 overflow-hidden rounded-card border border-border bg-card">
      <div className="w-full bg-skeleton-image" style={{ aspectRatio: '4 / 3' }} />
      <div className="p-4">
        <div className="rounded-field bg-sunken" style={{ height: 17 }} />
        <div className="mt-2 rounded-field bg-sunken" style={{ height: 13, width: '40%' }} />
      </div>
    </section>
  )
}

/**
 * Bandeau d'après-enregistrement — planche 09 : « Rangé dans Recettes · Changer. Corriger =
 * 1 appui », et « Changer ouvre une feuille de 6 grosses cibles de catégorie ».
 *
 * Une note n'a pas de domaine, donc rien à deviner : elle arrive toujours « à classer ». Plutôt
 * que de renvoyer l'utilisateur vers un tri ultérieur, les six cibles sont offertes ici — un
 * appui suffit, et ne rien faire reste parfaitement valable. L'ajout tient toujours en deux
 * appuis : ce troisième est facultatif.
 *
 * L'annulation des quatre secondes exigée par la section 06 reste disponible pendant que le
 * bandeau est ouvert ; passé ce délai, le bouton devient une simple fermeture.
 */
function BandeauEnregistre({
  spaceId,
  item,
  onClose,
}: {
  spaceId: string
  item: Item
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [range, setRange] = useState(item.categoryId)
  const [changer, setChanger] = useState(item.categoryId === null)

  const { data: categories } = useQuery<readonly Category[]>({
    queryKey: ['espace', spaceId, 'categories'],
    queryFn: () => api.space(spaceId).then((detail) => detail.categories),
    staleTime: 60_000,
  })

  const rafraichir = () => invaliderFiches(queryClient, spaceId)

  const classer = useMutation({
    mutationFn: (categoryId: string) => api.patchItem(spaceId, item.id, { categoryId }),
    onSuccess: async (fiche) => {
      setRange(fiche.categoryId)
      setChanger(false)
      navigator.vibrate?.(10)
      await rafraichir()
    },
  })

  const annuler = useMutation({
    mutationFn: () => api.deleteItem(spaceId, item.id),
    onSuccess: async () => {
      await rafraichir()
      onClose()
    },
  })

  const rangee = categories?.find((category) => category.id === range)

  return (
    <Feuille onClose={onClose}>
      <p className="text-card-title font-semibold">
        {rangee ? `Rangé dans ${rangee.name}` : 'Enregistré · à classer'}
      </p>
      <p className="mt-1 text-meta text-ink-muted">{item.title}</p>

      <Baptiser spaceId={spaceId} item={item} />

      {rangee && !changer ? (
        <button
          type="button"
          onClick={() => setChanger(true)}
          className="mt-3 text-meta font-semibold text-accent"
        >
          Changer
        </button>
      ) : null}

      {changer ? (
        <ul className="mt-4 grid grid-cols-2" style={{ gap: 12 }}>
          {categories?.map((category) => (
            <li key={category.id}>
              <button
                type="button"
                disabled={classer.isPending}
                onClick={() => classer.mutate(category.id)}
                className="flex w-full items-center gap-2 rounded-card p-3 text-left disabled:opacity-60"
                style={{
                  background: `var(--color-cat-${category.hue}-fill)`,
                  minHeight: 48,
                }}
              >
                <CategoryMark initials={category.initials} hue={category.hue} size="small" />
                <span className="text-meta font-semibold">{category.name}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Deux boutons distincts plutôt qu'un seul qui change de sens au bout de quatre secondes.
          La section 06 exige que le rangement soit annulable, pas qu'un décompte défile : le
          compteur ne faisait qu'ajouter du bruit et presser l'utilisateur. L'annulation reste
          possible tant que le bandeau est ouvert. */}
      <button
        type="button"
        onClick={() => onClose()}
        className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card"
        style={{ height: 56 }}
      >
        Fermer
      </button>

      <button
        type="button"
        onClick={() => annuler.mutate()}
        className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
        style={{ height: 56 }}
      >
        Annuler l'ajout
      </button>
    </Feuille>
  )
}

/**
 * Donner un nom à une fiche dont le titre ne dit rien, au moment où on l'ajoute.
 *
 * Certains sites ne rendent que leur propre nom à un lecteur de métadonnées : la fiche s'appelle
 * « Facebook », et plus rien ne dit quelle recette c'est. La même remarque existait déjà sur la
 * fiche (correction S88), mais trop tard : on y retourne rarement, et la liste se remplit de
 * fiches qu'on ne reconnaît plus.
 *
 * Ici, c'est le seul moment où l'on sait encore de quoi il s'agit — on vient de le coller.
 * Facultatif : ne rien faire reste valable, et l'ajout tient toujours en deux appuis.
 *
 * Les titres simplement <b>bruités</b> — « 2 K vues · 688 réactions | … » — ne passent pas par
 * ici : le serveur les a déjà nettoyés à l'enregistrement (L118).
 */
function Baptiser({ spaceId, item }: { spaceId: string; item: Item }) {
  const queryClient = useQueryClient()
  const [nom, setNom] = useState('')
  const [renomme, setRenomme] = useState(false)

  const renommer = useMutation({
    mutationFn: () => api.patchItem(spaceId, item.id, { title: nom.trim() }),
    onSuccess: async () => {
      setRenomme(true)
      await invaliderFiches(queryClient, spaceId)
    },
  })

  if (renomme) {
    return <p className="mt-3 text-meta text-accent">Renommée « {nom.trim()} ».</p>
  }

  if (!titreGenerique(item.title, item.domain)) {
    return null
  }

  return (
    <section className="mt-4 rounded-field border border-dashed border-border p-3">
      <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="baptiser">
        Ce titre ne dit pas ce que c'est
      </label>

      <div className="mt-2 flex gap-2">
        <input
          id="baptiser"
          value={nom}
          onChange={(evenement) => setNom(evenement.target.value)}
          onKeyDown={(evenement) => {
            if (evenement.key === 'Enter' && nom.trim().length > 0) {
              renommer.mutate()
            }
          }}
          placeholder="Salade de chou rouge"
          maxLength={300}
          className="grow rounded-field border border-border bg-card px-3 outline-none"
          style={{ height: 48, fontSize: 16 }}
        />
        <button
          type="button"
          disabled={nom.trim().length === 0 || renommer.isPending}
          onClick={() => renommer.mutate()}
          className="shrink-0 rounded-button bg-accent px-4 text-meta font-semibold text-card disabled:opacity-40"
          style={{ height: 48 }}
        >
          Nommer
        </button>
      </div>
    </section>
  )
}

/** Feuille modale : entrée par le bas, 260 ms, sortie douce (section 03.6). */
function Feuille({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-10 flex items-end justify-center" style={{ background: 'rgb(26 29 27 / 0.35)' }}>
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0"
        tabIndex={-1}
      />
      <section
        className="relative w-full rounded-sheet bg-surface p-4 shadow-sheet"
        style={{
          maxWidth: 'var(--layout-column)',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
          animation: `monter var(--duration-sheet) ease-out`,
        }}
      >
        {children}
      </section>
    </div>
  )
}
